import React, { useEffect, useState } from "react";
import { X, ChevronRight, ChevronLeft } from "lucide-react";

const ONBOARDING_KEY = "gestiondek_onboarding_stepper_v1";

/**
 * Mini-onboarding 5 étapes qui apparaît au premier accès d'un EventDetail.
 * Stocke un flag dans localStorage pour ne pas réapparaître.
 *
 * Usage : <StepperOnboarding /> à monter dans EventDetail.
 */
export default function StepperOnboarding({ forceShow = false, onDone }) {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (forceShow) {
      setVisible(true);
      setStep(0);
      return;
    }
    try {
      const done = localStorage.getItem(ONBOARDING_KEY);
      if (!done) setVisible(true);
    } catch {}
  }, [forceShow]);

  const steps = [
    {
      title: "Bienvenue dans la gestion d'événement",
      body: "Le stepper en haut de la page guide l'événement de A à Z. Voici comment l'utiliser en 4 points.",
    },
    {
      title: "1. L'étape active est mise en évidence",
      body: "Le système détecte automatiquement où tu en es. L'étape entourée en noir est ta prochaine action recommandée.",
    },
    {
      title: "2. Les boutons sont contextuels",
      body: "Sous le stepper, la barre d'actions n'affiche QUE les boutons pertinents pour l'étape active — fini la rangée massive de boutons.",
    },
    {
      title: "3. Tu peux cliquer sur n'importe quelle étape",
      body: "Clique sur une étape (même future) pour afficher ses boutons. Une bordure pointillée + un bouton « ← Étape actuelle » te permettent de revenir au point réel.",
    },
    {
      title: "4. Le workflow recommandé",
      body: "1) Configurer · 2) SMS de dispos · 3) Bâtir l'horaire · 4) Assigner · 5) Diffuser aux officiels · 6) Confirmations · 7) Fermer · 8) Paie + Facture.",
    },
  ];

  const close = () => {
    try {
      localStorage.setItem(ONBOARDING_KEY, "1");
    } catch {}
    setVisible(false);
    onDone && onDone();
  };

  if (!visible) return null;
  const isLast = step === steps.length - 1;
  const s = steps[step];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/65 backdrop-blur-sm p-4"
      data-testid="onboarding-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="card-flat max-w-md w-full relative"
        style={{ background: "var(--surface)", borderColor: "var(--border-strong)" }}
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-[var(--surface-3)] transition-colors"
          aria-label="Fermer"
          data-testid="onboarding-close-btn"
        >
          <X size={18} strokeWidth={2.2} />
        </button>

        <div className="label mb-2">
          Étape {step + 1} sur {steps.length}
        </div>
        <h3 className="text-xl font-bold mb-2 tracking-tight pr-6" data-testid="onboarding-title">
          {s.title}
        </h3>
        <p className="text-sm text-[var(--muted)] leading-relaxed" data-testid="onboarding-body">
          {s.body}
        </p>

        {/* Dots */}
        <div className="flex gap-1.5 mt-5 mb-5">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? "w-8 bg-[var(--text)]" : "w-1.5 bg-[var(--border)]"
              }`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          {step > 0 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="btn-ghost"
              data-testid="onboarding-prev-btn"
            >
              <ChevronLeft size={14} strokeWidth={2.5} /> Précédent
            </button>
          ) : (
            <button
              onClick={close}
              className="btn-ghost"
              data-testid="onboarding-skip-btn"
            >
              Passer
            </button>
          )}
          {isLast ? (
            <button
              onClick={close}
              className="btn-primary"
              data-testid="onboarding-done-btn"
            >
              C'est compris
            </button>
          ) : (
            <button
              onClick={() => setStep(step + 1)}
              className="btn-primary"
              data-testid="onboarding-next-btn"
            >
              Suivant <ChevronRight size={14} strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Bouton "?" pour réafficher l'onboarding manuellement. */
export function StepperOnboardingButton() {
  const [show, setShow] = useState(false);
  return (
    <>
      <button
        onClick={() => setShow(true)}
        className="btn-ghost !px-2"
        title="Voir le tutoriel du stepper"
        data-testid="onboarding-open-btn"
      >
        ?
      </button>
      {show && <StepperOnboarding forceShow onDone={() => setShow(false)} />}
    </>
  );
}
